import { writable } from "./svelte";

/**
 * @class A Svelte store that provides utility methods for adding/removing/toggling a given class on an element.
 * Svelte components can then execute reactive behaviors in response to whether the class is present using $store syntax.
 * It will automatically monitor changes in the element's class list using a MutationObserver to stay in sync with the DOM
 * if, say, an external factor outside the Svelte store mutates the element.
 */
export class ReactiveClassStore {
	/** @type {Function?} */
	_set = null;
	/** @type {string} */
	_class = "";
	/** @type {Element?} */
	_element = null;
	/** @type {import("./private").IReactiveClassState?} */
	_state = null;

	static #attributes = ["class"];

	/**
	 * @param {Element} element 
	 * @param {string} className 
	 */
	constructor(element, className) {
		this._class = className;
		this._element = element;
		this._state = { triggered: false, observer: null };
		let _state = this._state;

		const { subscribe, set } = writable(false, set => {

			if (_state.observer === null) {
				_state.observer = new MutationObserver(entries => {
					if (_state.triggered) {
						_state.triggered = false;
						return;
					}
					const entry = /** @type {HTMLBodyElement} */ (entries[0].target);
					set(entry.classList.contains(className));
				});
				_state.observer.observe(element, { attributeFilter: ReactiveClassStore.#attributes });
			}

			return () => {
				_state.observer?.disconnect();
				_state.observer = null;
			}
		});

		this.subscribe = subscribe;
		this._set = set;
	}

	get className() {
		return this._class;
	}

	get element() {
		return this._element;
	}

	/** @param {boolean} value */
	set(value) {
		this.#assign(value);
	}

	on() {
		this.#assign(true);
	}

	off() {
		this.#assign(false);
	}

	toggle() {
		return this.#assign();
	}

	/** @param {undefined | boolean} value */
	#assign(value = undefined) {
		const result = this._element?.classList.toggle(this._class, value);
		this._set?.(result);
	}
}

export default ReactiveClassStore;